(function () {
    const API_VERSION = '2.0';
    const ORDER_SOURCE = 'web';
    const SHARED_CHECKOUT_PENDING_KEY = 'mimeo_shared_checkout_pending';
    const SHARED_CHECKOUT_JUST_CLEARED_KEY = 'mimeo_shared_checkout_just_cleared';
    const SHARED_CHECKOUT_ORIGIN_BY_ME = 'shared-by-me';
    const SHARED_CHECKOUT_ORIGIN_WITH_ME = 'shared-with-me';

    function isSharedCheckoutOrigin(origin) {
        return origin === SHARED_CHECKOUT_ORIGIN_BY_ME || origin === SHARED_CHECKOUT_ORIGIN_WITH_ME;
    }

    function createSharedCheckoutToken() {
        return 'sp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    }

    function createHttpError(status, message, context) {
        const error = new Error(message || `HTTP ${status}`);
        error.status = Number(status) || 0;
        error.context = context || {};
        return error;
    }

    function objectToQueryParam(obj) {
        return Object.keys(obj)
            .filter((key) => obj[key] !== undefined && obj[key] !== null)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
            .join('&');
    }

    function getAuthToken() {
        // Use authState for token access (supports auto-refresh via async methods)
        if (window.authState && typeof window.authState.getTokenSync === 'function') {
            const token = window.authState.getTokenSync();
            if (!token) {
                throw new Error('No auth token found');
            }
            return token;
        }
        // Fallback to direct localStorage (for backwards compatibility)
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
            throw new Error('No auth token found');
        }
        return authToken;
    }

    async function getProjects({
        apiBaseUrl,
        currency,
        region,
        culture,
        userEmail = '',
        id='',
        endpoint = 'invitee',
        includeInvitees = false
    }) {
        const reqObj = {
            userEmail,
            'run-context-currency': currency,
            'run-context-region': region,
            culture,
            includeInvitees,
            shareProjectId: id
        };

        if (id) {
            reqObj.id = id;
        }

        const queryParams = objectToQueryParam(reqObj);
        const normalizedEndpoint =`/share/projects/${endpoint}`;
        const requestUrl = `${apiBaseUrl}${normalizedEndpoint}?${queryParams}`;

        if (!apiBaseUrl) {
            throw createHttpError(500, 'Shared projects API is not configured', {
                endpoint: normalizedEndpoint,
                reason: 'missing-api-base-url'
            });
        }

        let response;
        if (window.authState && typeof window.authState.authenticatedFetch === 'function') {
            // Use authenticatedFetch for automatic token refresh and 401 retry
            response = await window.authState.authenticatedFetch(requestUrl, {
                headers: {
                    'api-version': API_VERSION
                }
            });
        } else {
            const token = getAuthToken();
            response = await fetch(requestUrl, {
                headers: {
                    'api-version': API_VERSION,
                    'Authorization': `Bearer ${token}`
                }
            });
        }

        if (!response.ok) {
            throw createHttpError(response.status, `Shared projects request failed with status ${response.status}`, {
                endpoint: normalizedEndpoint,
                requestUrl
            });
        }

        return response.json();
    }

    function getMimeoUserIdFromAuthToken(authToken) {
        if (!authToken) return '';

        try {
            const payloadPart = authToken.split('.')[1];
            if (!payloadPart) return '';

            const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
            const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
            const jsonPayload = decodeURIComponent(
                atob(paddedBase64).split('').map((char) => {
                    return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
                }).join('')
            );
            const payload = JSON.parse(jsonPayload);

            const id = payload.mimophotosoid ?? payload['mimeophotosoid'];
            return id ? String(id) : '';
        } catch (error) {
            console.error('Error parsing auth token:', error);
            return '';
        }
    }

    window.SharedProjectsService = {
        getProjects,
        objectToQueryParam,
        getAuthToken,
        getMimeoUserIdFromAuthToken,
        createHttpError
    };

    // ── Shared Order Creation Logic ──

    function isCalendarSku(sku) {
        return /^\w{2}-CAL.+/i.test(String(sku || ''));
    }

    function getOrderQueryParams() {
        const config = window.sharedProjectConfig || {};
        const region = config.region || 'US';
        const cultureCode = config.culture || 'en';
        const culture = String(cultureCode).includes('-') ? cultureCode : `${cultureCode}-${region}`;

        return objectToQueryParam({
            culture,
            'run-context-currency': config.currency || 'USD',
            'run-context-region': region,
            'run-context-source': ORDER_SOURCE
        });
    }

    async function createShopifyOrderAndRedirect(project, options = {}) {
        var checkoutOrigin = options.checkoutOrigin;
        var isSharedOrigin = isSharedCheckoutOrigin(checkoutOrigin);
        var sharedCheckoutToken = isSharedOrigin ? createSharedCheckoutToken() : '';
        // Get token via authState for user ID extraction
        const customerAccessToken = window.authState?.getTokenSync() || localStorage.getItem('auth_token');
        const mimeoUserId = getMimeoUserIdFromAuthToken(customerAccessToken);
        if (!mimeoUserId) {
            throw new Error('Unable to resolve user id from auth token');
        }
        let sku = isCalendarSku(project.sku)? `${project.sku}-${project.pageCount}` : project.sku;
        const timestamp = project.projectUpdatedDate || project.projectShareDate || new Date().toISOString();
        const projectName = project.customGreetingsHeading || project.productName || String(timestamp);
        const pageCount = Number.parseInt(project.pageCount, 10);
        const hasValidPageCount = Number.isFinite(pageCount);
        const additionalSheetCount = hasValidPageCount ? (Math.max(pageCount - 20, 0))/2 : 0;

        const orderItem = {
            productId: project.productId,
            themeUrl: project.themeUrl,
            plu: sku,
            quantity:  1,
            properties: {
                creationDate: timestamp,
                projectName,
                shareProjectId: project.shareProjectId
            }
        };
        if (isSharedOrigin) {
            orderItem.properties._checkoutOrigin = checkoutOrigin;
            orderItem.properties._sharedCheckoutToken = sharedCheckoutToken;
            orderItem.properties.checkoutOrigin = checkoutOrigin;
            orderItem.properties.sharedCheckoutToken = sharedCheckoutToken;
        }
        if (additionalSheetCount > 0 && !isCalendarSku(project.sku)) {
            orderItem.photobookData = {
                additionalSheetCount
            };
        }

        const body = {
            storeData: {
                userId: mimeoUserId
            },
            projectId: project.projectId,
            properties: {
                creationDate: timestamp,
                projectName,
                shareProjectId: project.shareProjectId
            },
            items: [orderItem]
        };
        if (isSharedOrigin) {
            body.properties._checkoutOrigin = checkoutOrigin;
            body.properties._sharedCheckoutToken = sharedCheckoutToken;
            body.properties.checkoutOrigin = checkoutOrigin;
            body.properties.sharedCheckoutToken = sharedCheckoutToken;
        }

        const queryParams = getOrderQueryParams();
        const response = await fetch(`${window.API_BASE_URL}/orders/shopify/create?${queryParams}`, {
            method: 'POST',
            headers: {
                authorization: window.SHOPIFY_CREATE_ORDER_AUTH || '',
                'content-type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error('Failed to create order');
        }

        const responseData = await response.json();
        if (!responseData.redirectUrl) {
            throw new Error('Order redirect URL not found in response');
        }

        if (isSharedOrigin) {
            try {
                sessionStorage.removeItem(SHARED_CHECKOUT_JUST_CLEARED_KEY);
                sessionStorage.setItem(
                    SHARED_CHECKOUT_PENDING_KEY,
                    JSON.stringify({
                        source: 'shared-project-checkout',
                        checkoutOrigin: checkoutOrigin,
                        checkoutToken: sharedCheckoutToken,
                        shareProjectId: project.shareProjectId || '',
                        createdAt: Date.now()
                    })
                );
            } catch (_error) {
                // Non-blocking: checkout redirect should continue even if storage is unavailable.
            }
        }

        window.location.href = responseData.redirectUrl;
    }

    /**
     * Create an order for a shared project by resolving the SKU,
     * building the cart line items, and adding them to the Shopify cart.
     * @param {Object} project - The shared project object (must have sku, projectId, shareProjectId, pageCount).
     * @returns {Promise<void>}
     */
    async function createOrder(project, options = {}) {
        if (!project || !project.sku) {
            return;
        }

        // Check authentication via authState
        const customerAccessToken = window.authState?.getTokenSync() || localStorage.getItem('auth_token');
        if (!customerAccessToken) {
            return;
        }

        await createShopifyOrderAndRedirect(project, options);
    }

    // Expose order utilities on the shared service
    window.SharedProjectsService.createOrder = createOrder;
    window.SharedProjectsService.createShopifyOrderAndRedirect = createShopifyOrderAndRedirect;
})();
